import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import {
  Inbox as InboxIcon, Loader2, Mail, Paperclip, FileText, Sparkles, CheckCircle2,
  XCircle, Copy, Search, Download, ExternalLink, ChevronDown, ChevronUp, MessageSquareQuote,
  Volume2, VolumeX, Bell, BellOff, Reply, Send, X as XIcon, Save, Trash2, Eye,
} from "lucide-react";
import { api, formatFileSize } from "@/lib/api";
import MobileInputWithWA from "@/components/MobileInputWithWA";
import { useAttachmentPreview } from "@/hooks/useAttachmentPreview";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { downloadWithProgress, runExtractionWithProgress } from "@/lib/downloadWithProgress";

// 🆕 Sanitizzatore HTML per rendering sicuro delle email ricevute/inviate.
// Blocca <script>, <iframe>, gestori "on*" e URL javascript:. Consente immagini via cid/data/https,
// tabelle, stili inline (necessari per newsletter/preventivi HTML).
const sanitizeEmailHtml = (html) => DOMPurify.sanitize(html || "", {
  ALLOWED_TAGS: [
    "a", "b", "blockquote", "br", "code", "div", "em", "font", "h1", "h2", "h3", "h4", "h5", "h6",
    "hr", "i", "img", "li", "ol", "p", "pre", "small", "span", "strong", "sub", "sup",
    "table", "tbody", "td", "tfoot", "th", "thead", "tr", "u", "ul",
  ],
  ALLOWED_ATTR: ["href", "src", "alt", "title", "target", "rel", "style", "class", "align", "border", "cellpadding", "cellspacing", "width", "height", "colspan", "rowspan", "bgcolor", "color", "face", "size"],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel|cid|data):|[^:]*$)/i,
  FORBID_TAGS: ["script", "iframe", "object", "embed", "form", "input", "button", "textarea", "style", "link", "meta"],
  FORBID_ATTR: ["onerror", "onload", "onclick", "onmouseover", "onfocus", "onblur", "onsubmit"],
});

const REPLY_QUILL_MODULES = {
  toolbar: [
    ["bold", "italic", "underline", "strike"],
    [{ color: [] }, { background: [] }],
    [{ list: "ordered" }, { list: "bullet" }],
    [{ align: [] }],
    ["link"],
    ["clean"],
  ],
};
const REPLY_QUILL_FORMATS = ["bold", "italic", "underline", "strike", "color", "background", "list", "align", "link"];

const STATUS_BADGE = {
  pending: { lbl: "Da elaborare", cls: "bg-amber-100 text-amber-700 border-amber-300" },
  processed: { lbl: "Elaborata", cls: "bg-emerald-100 text-emerald-700 border-emerald-300" },
  discarded: { lbl: "Scartata", cls: "bg-slate-100 text-slate-600 border-slate-300" },
};

export default function Inbox() {
  const nav = useNavigate();
  // 🆕 Feb 2026 — anteprima inline PDF/immagini allegati email
  const { openPreview: openAttachmentPreview, PreviewDialog: AttachmentPreviewDialog } = useAttachmentPreview();
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState("pending");
  // 🆕 Modalità di visualizzazione: "list" (piatta) o "thread" (conversazioni raggruppate)
  const [viewMode, setViewMode] = useState(() => {
    try { return localStorage.getItem("inbox_view_mode") || "list"; } catch { return "list"; }
  });
  const [threads, setThreads] = useState([]);
  const [expandedThreads, setExpandedThreads] = useState(new Set());
  const [searchText, setSearchText] = useState("");
  const [busy, setBusy] = useState(false);
  const [bodyViewMode, setBodyViewMode] = useState("html"); // 🆕 "html" | "text" — modalità corpo email selezionata
  const [selectedIds, setSelectedIds] = useState(new Set()); // 🆕 selezione multipla per bulk delete
  const [myEmail, setMyEmail] = useState(null);
  const [extracted, setExtracted] = useState(null); // dati estratti dall'AI
  const [extractingAttId, setExtractingAttId] = useState(null);
  // 🆕 Dialog "Salva allegato nel Preventivo": { att, quote_id, filename }
  const [saveAttDialog, setSaveAttDialog] = useState(null);
  const [savingAttToQuote, setSavingAttToQuote] = useState(false);
  // 🆕 Feb 2026 — Info dei preventivi collegati (numero + nominativo cliente) per il selettore
  const [linkedQuotesInfo, setLinkedQuotesInfo] = useState({});
  const [createDlg, setCreateDlg] = useState(null); // {step: 1|2, ...}
  const [clientsOptions, setClientsOptions] = useState([]);
  const [clientSearch, setClientSearch] = useState("");
  const [templateOpen, setTemplateOpen] = useState(false);
  const [templateChannel, setTemplateChannel] = useState("email"); // "email" | "whatsapp"
  const [trashCount, setTrashCount] = useState(0); // 🆕 conteggio email nel cestino per badge tab
  const loadRequestId = useRef(0); // 🆕 anti-race: ignora risposte più vecchie del filtro corrente

  const load = async () => {
    // 🆕 Feb 2026 — anti race-condition: assegna un ID incrementale ad ogni fetch.
    // Se durante il fetch l'utente cambia tab (es. da "pending" a "trash"), le risposte
    // "stale" del fetch precedente vengono scartate: setEmails viene chiamato SOLO se
    // il nostro ID corrisponde ancora all'ultimo trigger.
    const myReqId = ++loadRequestId.current;
    setLoading(true);
    try {
      // 🆕 Cestino: fetch da endpoint dedicato /messages/trash
      if (statusFilter === "trash") {
        const { data } = await api.get(`/messages/trash?limit=300`);
        if (myReqId !== loadRequestId.current) return;
        const items = Array.isArray(data?.items) ? data.items : [];
        const normalized = items.map((m) => ({
          id: m.id,
          direction: m.direction,
          subject: m.subject,
          from_addr: m.direction === "in" ? m.counterparty : null,
          to: m.direction === "out" ? m.counterparty : null,
          received_at: m.date,
          deleted_at: m.deleted_at,
          status: "deleted",
          body_text: m.body_text,
          attachments: m.attachments || [],
          message_id: m.message_id,
        }));
        setEmails(normalized);
        setThreads([]);
        setTrashCount(items.length);
      } else if (viewMode === "thread") {
        const { data } = await api.get(`/messages?group=thread&limit=300`);
        if (myReqId !== loadRequestId.current) return;
        setThreads(Array.isArray(data?.threads) ? data.threads : []);
        setEmails([]);
      } else {
        const { data } = await api.get(`/messages?limit=300`);
        if (myReqId !== loadRequestId.current) return;
        const items = Array.isArray(data?.items) ? data.items : [];
        // Normalizza in un formato compatibile con l'UI Inbox esistente
        const normalized = items.map((m) => ({
          id: m.id,
          direction: m.direction,  // "in" | "out"
          subject: m.subject,
          from_addr: m.direction === "in" ? m.counterparty : null,
          to: m.direction === "out" ? m.counterparty : null,
          received_at: m.date,
          status: m.status || (m.direction === "out" ? "sent" : "pending"),
          body_text: m.body_text,
          attachments: m.attachments || [],
          message_id: m.message_id,
          in_reply_to: m.in_reply_to,
          references: m.references,
          linked_quote_id: m.linked_quote_id,
          linked_client_id: m.linked_client_id,
          linked_claim_id: m.linked_claim_id,
          email_type: m.email_type,
        }));
        // Applica filtro di stato lato client (perché ora unifichiamo due sorgenti)
        const filtered = statusFilter === "all"
          ? normalized
          : statusFilter === "sent"
            ? normalized.filter((e) => e.direction === "out")
            : normalized.filter((e) => e.direction === "in" && e.status === statusFilter);
        setEmails(filtered);
        setThreads([]);
      }
    } catch (e) {
      if (loadRequestId.current === myReqId) toast.error(e?.response?.data?.detail || "Errore caricamento messaggi");
    }
    finally { if (loadRequestId.current === myReqId) setLoading(false); }
  };

  useEffect(() => { load(); }, [statusFilter, viewMode]);
  useEffect(() => {
    api.get("/inbox/my-email").then(({ data }) => setMyEmail(data)).catch(() => {});
  }, []);

  // Auto-detect nuove email: polling silenzioso ogni 60s del conteggio "pending"
  // Se aumenta dal valore precedente → segnala con animazione + toast.
  const [hasNewSignal, setHasNewSignal] = useState(false);
  const [lastPendingCount, setLastPendingCount] = useState(null);
  const [soundEnabled, setSoundEnabled] = useState(() => {
    try { return localStorage.getItem("inbox_sound_enabled") !== "false"; }
    catch { return true; }
  });
  const [pushEnabled, setPushEnabled] = useState(() => {
    try {
      if (!("Notification" in window)) return false;
      const pref = localStorage.getItem("inbox_push_enabled");
      // Default ON solo se permission già concessa
      if (pref === null) return Notification.permission === "granted";
      return pref === "true" && Notification.permission === "granted";
    } catch { return false; }
  });

  const playDing = () => {
    if (!soundEnabled) return;
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      const ctx = new AudioCtx();
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.type = "sine";
      osc.frequency.setValueAtTime(880, ctx.currentTime);
      osc.frequency.exponentialRampToValueAtTime(1320, ctx.currentTime + 0.06);
      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.03);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.4);
      osc.start(ctx.currentTime);
      osc.stop(ctx.currentTime + 0.42);
      setTimeout(() => { try { ctx.close(); } catch { /* ignore */ } }, 600);
    } catch { /* silent */ }
  };

  const showPushNotification = (count, lastEmail) => {
    if (!pushEnabled || !("Notification" in window) || Notification.permission !== "granted") return;
    try {
      const title = count === 1
        ? "📨 Nuova email ricevuta"
        : `📨 ${count} nuove email ricevute`;
      const bodyParts = [];
      if (lastEmail?.from_addr) bodyParts.push(`Da: ${lastEmail.from_addr}`);
      if (lastEmail?.subject) bodyParts.push(`Oggetto: ${lastEmail.subject}`);
      const body = bodyParts.length ? bodyParts.join("\n") : "Apri la pagina Inbox per i dettagli.";
      const notif = new Notification(title, {
        body,
        tag: "inbox-new-email",
        renotify: true,
        icon: "/favicon.ico",
      });
      notif.onclick = () => {
        window.focus();
        try { nav("/inbox"); } catch { /* ignore */ }
        notif.close();
      };
    } catch { /* silent */ }
  };

  const toggleSound = () => {
    const next = !soundEnabled;
    setSoundEnabled(next);
    try { localStorage.setItem("inbox_sound_enabled", String(next)); } catch { /* ignore */ }
    if (next) {
      playDing();
      toast.success("Suono notifica attivato");
    } else {
      toast.info("Suono notifica disattivato");
    }
  };

  const togglePush = async () => {
    if (!("Notification" in window)) {
      toast.error("Browser non supporta le notifiche push");
      return;
    }
    if (pushEnabled) {
      setPushEnabled(false);
      try { localStorage.setItem("inbox_push_enabled", "false"); } catch { /* ignore */ }
      toast.info("Notifiche desktop disattivate");
      return;
    }
    // Richiedi permission se necessario
    let perm = Notification.permission;
    if (perm === "default") {
      perm = await Notification.requestPermission();
    }
    if (perm === "granted") {
      setPushEnabled(true);
      try { localStorage.setItem("inbox_push_enabled", "true"); } catch { /* ignore */ }
      toast.success("Notifiche desktop attivate");
      try {
        const n = new Notification("✅ Notifiche attivate", { body: "Riceverai un avviso desktop quando arrivano nuove email." });
        setTimeout(() => n.close(), 5000);
      } catch { /* ignore */ }
    } else if (perm === "denied") {
      toast.error("Permesso negato dal browser. Per attivarle: clicca sull'icona del lucchetto a sinistra dell'URL → Notifiche → Consenti.");
    }
  };

  useEffect(() => {
    let cancelled = false;
    const originalTitle = "Inbox documenti · Assicura";
    const fetchCount = async () => {
      try {
        const { data } = await api.get("/inbox?status_filter=pending");
        if (cancelled) return;
        const list = data || [];
        const count = list.length;
        if (lastPendingCount !== null && count > lastPendingCount) {
          setHasNewSignal(true);
          const delta = count - lastPendingCount;
          toast.success(`📨 ${delta} nuova/e email ricevuta/e — badge aggiornati automaticamente`, { duration: 6000 });
          playDing();
          // Notifica desktop con preview ultima email
          const latest = list[0]; // l'API ordina già per received_at DESC
          showPushNotification(delta, latest);
        }
        setLastPendingCount(count);
        // Aggiorna titolo tab del browser come fa Gmail/Facebook
        document.title = count > 0 ? `(${count}) ${originalTitle}` : originalTitle;
        // 🛠 Fix contatori "fermi": ricarica sempre la lista completa (con il filtro corrente)
        // così i badge Da elaborare / Elaborate / Scartate restano sincronizzati con il DB.
        // ⚠ NON toccare emails quando siamo sul tab Cestino: usa un endpoint diverso (/messages/trash)
        // altrimenti sovrascriviamo la lista trash con le email attive.
        if (statusFilter !== "trash") {
          try {
            const { data: full } = await api.get(`/inbox?status_filter=${statusFilter === "all" ? "" : statusFilter}`);
            if (!cancelled) setEmails(full || []);
          } catch { /* silent */ }
        }
      } catch { /* silent */ }
    };
    fetchCount(); // primo run
    const interval = setInterval(fetchCount, 60000); // ogni 60s
    return () => {
      cancelled = true;
      clearInterval(interval);
      document.title = originalTitle;
    };
  }, [lastPendingCount, soundEnabled, pushEnabled, statusFilter]);

  const loadDetail = async (id, direction = "in") => {
    setSelectedId(id);
    setSelected(null);
    setExtracted(null);
    try {
      if (direction === "out") {
        // 🆕 Le email inviate: fetch del documento completo (con body_html) da /outbox/{id}
        try {
          const { data } = await api.get(`/outbox/${id}`);
          setSelected({
            id: data.id,
            direction: "out",
            subject: data.subject,
            from_addr: null,
            to: (data.to_list || data.to || []).join?.(", ") || data.to || null,
            received_at: data.sent_at || data.received_at,
            body_text: data.body_text || "",
            body_html: data.body_html || null,
            attachments: data.attachments || [],
            status: "sent",
            message_id: data.message_id,
            linked_quote_id: data.linked_quote_id,
            linked_client_id: data.linked_client_id,
          });
          return;
        } catch {
          // fallback alla riga in memoria se il dettaglio non è disponibile
          const row = emails.find((e) => e.id === id);
          if (row) {
            setSelected({
              id: row.id, direction: "out", subject: row.subject, from_addr: null, to: row.to,
              received_at: row.received_at, body_text: row.body_text, body_html: null,
              attachments: row.attachments || [], status: "sent", message_id: row.message_id,
              linked_quote_id: row.linked_quote_id, linked_client_id: row.linked_client_id,
            });
            return;
          }
        }
      }
      const { data } = await api.get(`/inbox/${id}`);
      setSelected({ ...data, direction: "in" });
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
  };

  const filtered = useMemo(() => {
    const s = searchText.trim().toLowerCase();
    if (!s) return emails;
    return emails.filter((e) =>
      (e.subject || "").toLowerCase().includes(s) ||
      (e.from_addr || "").toLowerCase().includes(s) ||
      (e.body_text || "").toLowerCase().includes(s)
    );
  }, [emails, searchText]);

  const counts = {
    pending: emails.filter((e) => e.direction !== "out" && e.status === "pending").length,
    processed: emails.filter((e) => e.direction !== "out" && e.status === "processed").length,
    discarded: emails.filter((e) => e.direction !== "out" && e.status === "discarded").length,
    sent: emails.filter((e) => e.direction === "out").length,
  };

  const copyEmail = () => {
    if (!myEmail?.address) return;
    navigator.clipboard.writeText(myEmail.address);
    toast.success("Email copiata negli appunti");
  };

  const [polling, setPolling] = useState(false);
  const [replyOpen, setReplyOpen] = useState(false);
  const [replyForm, setReplyForm] = useState({ to: "", cc: "", bcc: "", subject: "", body_html: "", attachment_ids: [], inbox_attachment_ids: [] });
  const [replyAttUploading, setReplyAttUploading] = useState(false);
  const [replyAttachments, setReplyAttachments] = useState([]); // [{id, filename, size}]
  const [replySending, setReplySending] = useState(false);

  const openReply = () => {
    if (!selected) return;
    // Estrae l'indirizzo email da "Nome <email@domain>"
    const fromAddr = selected.from_addr || "";
    const m = fromAddr.match(/<([^>]+)>/);
    const toEmail = m ? m[1] : fromAddr.trim();
    const origSubj = selected.subject || "";
    const reSubj = /^re:/i.test(origSubj) ? origSubj : `Re: ${origSubj}`;
    setReplyForm({
      to: toEmail,
      cc: "",
      bcc: "",
      subject: reSubj,
      body_html: `<p></p><br/><blockquote style="border-left:3px solid #cbd5e1;margin:0;padding-left:12px;color:#475569;">${(selected.body_text || "").replace(/</g, "&lt;").replace(/\n/g, "<br/>")}</blockquote>`,
      attachment_ids: [],
      inbox_attachment_ids: [],
    });
    setReplyAttachments([]);
    setReplyMode("reply");
    setReplyOpen(true);
  };

  const [replyMode, setReplyMode] = useState("reply"); // "reply" | "compose"
  const [composeClientSearch, setComposeClientSearch] = useState("");
  const [composeClientOptions, setComposeClientOptions] = useState([]);

  const openCompose = () => {
    setReplyForm({ to: "", cc: "", bcc: "", subject: "", body_html: "<p></p>", attachment_ids: [], inbox_attachment_ids: [] });
    setReplyAttachments([]);
    setComposeClientSearch("");
    setComposeClientOptions([]);
    setReplyMode("compose");
    setReplyOpen(true);
  };

  useEffect(() => {
    if (replyMode !== "compose") return;
    const q = composeClientSearch.trim();
    if (q.length < 2) { setComposeClientOptions([]); return; }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(`/clients?q=${encodeURIComponent(q)}&limit=10`);
        setComposeClientOptions((data || []).filter((c) => c.email));
      } catch { setComposeClientOptions([]); }
    }, 300);
    return () => clearTimeout(t);
  }, [composeClientSearch, replyMode]);

  // 🆕 Feb 2026 — Quando apro il dialog "Salva su preventivo", carico numero+cliente
  // per ciascuno dei preventivi collegati all'email selezionata
  useEffect(() => {
    if (!saveAttDialog) return;
    const ids = Array.isArray(selected?.linked_quote_ids) ? selected.linked_quote_ids : [];
    if (ids.length === 0) return;
    const missing = ids.filter((qid) => !linkedQuotesInfo[qid]);
    if (missing.length === 0) return;
    (async () => {
      try {
        const [act, tra] = await Promise.all([api.get("/quotes"), api.get("/quotes", { params: { trash: true } })]);
        const all = [...(Array.isArray(act.data) ? act.data : []), ...(Array.isArray(tra.data) ? tra.data : [])];
        const patch = {};
        for (const qid of missing) {
          const q = all.find((x) => x.id === qid);
          patch[qid] = q ? {
            number: q.quote_number || q.number || "",
            client_name: q.client_name || "",
            deleted: !!q.deleted_at,
          } : { number: "", client_name: "", deleted: false, missing: true };
        }
        setLinkedQuotesInfo((prev) => ({ ...prev, ...patch }));
      } catch { /* fallback silenzioso: il selettore mostrerà l'id troncato */ }
    })();
  }, [saveAttDialog, selected, linkedQuotesInfo]);

  const uploadReplyAttachment = async (file) => {
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("File troppo grande (max 15 MB)"); return; }
    setReplyAttUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/outbox/upload-attachment", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReplyAttachments((prev) => [...prev, { id: data.id, filename: data.filename, size: data.size }]);
      setReplyForm((prev) => ({ ...prev, attachment_ids: [...(prev.attachment_ids || []), data.id] }));
      toast.success(`Allegato caricato: ${data.filename}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore upload");
    } finally {
      setReplyAttUploading(false);
    }
  };

  const removeReplyAttachment = (id) => {
    setReplyAttachments((prev) => prev.filter((a) => a.id !== id));
    setReplyForm((prev) => ({ ...prev, attachment_ids: (prev.attachment_ids || []).filter((x) => x !== id) }));
  };

  const toggleInboxAttachmentForReply = (att_id) => {
    setReplyForm((prev) => {
      const cur = prev.inbox_attachment_ids || [];
      const next = cur.includes(att_id) ? cur.filter((x) => x !== att_id) : [...cur, att_id];
      return { ...prev, inbox_attachment_ids: next };
    });
  };

  const sendReply = async () => {
    if (!replyForm.to.trim()) { toast.error("Destinatario obbligatorio"); return; }
    if (!replyForm.subject.trim()) { toast.error("Oggetto obbligatorio"); return; }
    if (!replyForm.body_html || replyForm.body_html.replace(/<[^>]*>/g, "").trim().length === 0) {
      if (!window.confirm("Il messaggio è vuoto. Inviare comunque?")) return;
    }
    setReplySending(true);
    try {
      const payload = {
        to: replyForm.to,
        cc: replyForm.cc || null,
        bcc: replyForm.bcc || null,
        subject: replyForm.subject,
        body_html: replyForm.body_html,
        attachment_ids: replyForm.attachment_ids,
        inbox_attachment_ids: replyForm.inbox_attachment_ids,
        commercial: replyMode !== "reply" && !!replyForm.commercial,
      };
      if (replyMode === "reply" && selected?.id) {
        payload.reply_to_email_id = selected.id;
      }
      const { data: sent } = await api.post("/outbox/compose", payload);
      toast.success(replyMode === "reply" ? "Risposta inviata" : "Email inviata");
      if (sent?.excluded_no_consent?.length) toast.warning(`Esclusi senza consenso marketing: ${sent.excluded_no_consent.join(", ")}`, { duration: 8000 });
      setReplyOpen(false);
      load();
      if (replyMode === "reply") {
        setSelected(null);
        setSelectedId(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore invio");
    } finally {
      setReplySending(false);
    }
  };

  const pollNow = async () => {
    setPolling(true);
    try {
      const { data } = await api.post("/inbox/poll-now");
      const tot = data.agency_new_count || 0;
      if (tot > 0) {
        toast.success(`📨 ${tot} nuova/e email per la tua agenzia`);
      } else {
        toast.info("Nessuna nuova email per la tua agenzia in questo ciclo");
      }
      setHasNewSignal(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Errore polling");
    } finally {
      setPolling(false);
    }
  };

  const buildTemplate = (channel) => {
    const addr = myEmail?.address || "[indirizzo da configurare]";
    const agency = myEmail?.agency_name || "la nostra agenzia";
    if (channel === "whatsapp") {
      return `Ciao 👋, per processare la pratica assicurativa ti chiedo gentilmente di inviare i seguenti documenti via email a:

📧 ${addr}

Documenti richiesti:
• Libretto di circolazione (fronte/retro)
• Documento d'identità del contraente
• Eventuale visura/dichiarazione

Una volta ricevuti i documenti, ti contatterò con il preventivo entro 24h.

Grazie!
— ${agency}`;
    }
    // email template
    return `Oggetto: Documenti per preventivo assicurativo

Ciao,

per poter procedere con il preventivo ti chiedo di inviarmi i seguenti documenti in allegato all'indirizzo dedicato della nostra agenzia:

📧 ${addr}

Documenti richiesti:
• Libretto di circolazione del veicolo (fronte e retro)
• Carta d'identità o passaporto del contraente
• Eventuale visura aziendale (per polizze business)
• Polizza in scadenza (se rinnovo)

Importante: invia i documenti come allegati (PDF o foto JPG/PNG). Una volta ricevuti, il sistema estrarrà automaticamente i dati e ti contatterò con il preventivo personalizzato entro 24h.

Grazie per la collaborazione,
${agency}`;
  };

  const copyTemplate = () => {
    const txt = buildTemplate(templateChannel);
    navigator.clipboard.writeText(txt);
    toast.success(`Template ${templateChannel === "whatsapp" ? "WhatsApp" : "email"} copiato`);
  };

  const extractFromAttachment = async (attId) => {
    if (!selected) return;
    setExtractingAttId(attId);
    setExtracted(null);
    try {
      // 🆕 Feb 2026 — Barra di progresso durante l'estrazione AI del libretto
      const data = await runExtractionWithProgress({
        label: "Estrazione libretto in corso…",
        run: () => api.post(`/inbox/${selected.id}/extract-libretto?attachment_id=${attId}`).then((r) => r.data),
      });
      setExtracted(data?.extracted || {});
      toast.success("Dati estratti dal libretto");
    } catch (_e) {
      // Toast d'errore già mostrato da runExtractionWithProgress
    } finally { setExtractingAttId(null); }
  };

  const discardEmail = async () => {
    if (!selected) return;
    const ok = window.confirm(
      `⚠️ Spostare nel cestino questa email?\n\n` +
      `Oggetto: "${selected.subject || "(senza oggetto)"}"\n` +
      `Mittente: ${selected.from_addr || "—"}\n\n` +
      `L'email verrà marcata come "Scartata" e non apparirà più tra le Da elaborare.\n` +
      `Potrai recuperarla in qualsiasi momento cambiando il filtro stato in "Scartate".\n\n` +
      `Confermi lo spostamento nel cestino?`
    );
    if (!ok) return;
    setBusy(true);
    const discardedId = selected.id;
    const discardedSubject = selected.subject || "(senza oggetto)";
    try {
      await api.post(`/inbox/${discardedId}/discard`);
      // Toast con bottone Annulla (stile Gmail "Undo")
      toast.success(`Email spostata nel cestino: "${discardedSubject}"`, {
        duration: 8000,
        action: {
          label: "Annulla",
          onClick: async () => {
            try {
              await api.post(`/inbox/${discardedId}/restore`);
              toast.success("Email ripristinata");
              load();
            } catch (err) {
              toast.error(err?.response?.data?.detail || "Errore ripristino");
            }
          },
        },
      });
      setSelected(null); setSelectedId(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  const restoreEmail = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.post(`/inbox/${selected.id}/restore`);
      toast.success("Email ripristinata tra le Da elaborare");
      setSelected(null); setSelectedId(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
    finally { setBusy(false); }
  };

  // 🆕 Cancella email (in/out) — hard delete + cleanup allegati lato server.
  const deleteEmail = async () => {
    if (!selected) return;
    const isOut = selected.direction === "out";
    const label = isOut ? "email inviata" : "email ricevuta";
    if (!window.confirm(`Eliminare definitivamente questa ${label}?\n\nQuesta azione NON è reversibile e rimuoverà anche eventuali allegati collegati.`)) return;
    setBusy(true);
    try {
      const path = isOut ? `/outbox/${selected.id}` : `/inbox/${selected.id}`;
      await api.delete(path);
      toast.success(`${isOut ? "Email inviata" : "Email ricevuta"} eliminata`);
      setSelected(null); setSelectedId(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore eliminazione"); }
    finally { setBusy(false); }
  };

  // 🆕 Toggle checkbox singola riga (usa event.stopPropagation nell'onChange del checkbox)
  const toggleSelectedId = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // 🆕 Toggle "Seleziona tutte" (visibili con il filtro corrente)
  const toggleSelectAll = () => {
    const visibleIds = filtered.map((e) => e.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
    setSelectedIds(allSelected ? new Set() : new Set(visibleIds));
  };

  // 🆕 Bulk delete — separa ID inbox/outbox e chiama POST /messages/bulk-delete
  const bulkDeleteSelected = async () => {
    if (selectedIds.size === 0) return;
    const inbox_ids = [];
    const outbox_ids = [];
    for (const e of filtered) {
      if (selectedIds.has(e.id)) {
        if (e.direction === "out") outbox_ids.push(e.id);
        else inbox_ids.push(e.id);
      }
    }
    const isTrash = statusFilter === "trash";
    const verb = isTrash ? "eliminare DEFINITIVAMENTE" : "spostare nel cestino";
    if (!window.confirm(`Vuoi ${verb} ${selectedIds.size} email selezionate?\n\n📥 ${inbox_ids.length} ricevute\n📤 ${outbox_ids.length} inviate\n\n${isTrash ? "Azione irreversibile." : "Potrai ripristinarle dal cestino entro 30 giorni."}`)) return;
    setBusy(true);
    try {
      const path = isTrash ? "/messages/trash/bulk-hard-delete" : "/messages/bulk-delete";
      const { data } = await api.post(path, { inbox_ids, outbox_ids });
      toast.success(`${isTrash ? "Eliminate definitivamente" : "Spostate nel cestino"}: ${(data.deleted_inbox || 0) + (data.deleted_outbox || 0)} email`);
      setSelectedIds(new Set());
      if (selected && selectedIds.has(selected.id)) { setSelected(null); setSelectedId(null); }
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore eliminazione in blocco"); }
    finally { setBusy(false); }
  };

  const openCreateQuote = () => {
    if (!selected) return;
    // Pre-compila dati dal libretto se presenti
    const extr = extracted || {};
    setCreateDlg({
      step: 1,
      client_choice: "new",  // "new" | "existing"
      existing_client_id: "",
      new_client: {
        name: extr.owner_name || "",
        surname: extr.owner_surname || "",
        fiscal_code: extr.owner_fiscal_code || "",
        email: "", phone: "", address: "", city: "", cap: "", province: "",
      },
      quote_type: "auto",
      notes: selected.subject || "",
      attachment_ids: (selected.attachments || []).map((a) => a.id),  // tutti selezionati di default
      vehicle_data: extr,  // dati veicolo dal libretto
    });
    setClientsOptions([]); setClientSearch("");
  };

  const searchClients = async (q) => {
    setClientSearch(q);
    if (q.trim().length < 2) { setClientsOptions([]); return; }
    try {
      const { data } = await api.get(`/clients?q=${encodeURIComponent(q.trim())}&limit=10`);
      setClientsOptions((data?.items || data || []).slice(0, 10));
    } catch { /* ignore */ }
  };

  const confirmCreateQuote = async () => {
    if (!createDlg || !selected) return;
    const payload = {
      client_id: createDlg.client_choice === "existing" ? createDlg.existing_client_id : null,
      new_client: createDlg.client_choice === "new" ? createDlg.new_client : null,
      quote_type: createDlg.quote_type || "auto",
      notes: createDlg.notes || "",
      attachment_ids: createDlg.attachment_ids || [],
      vehicle_data: createDlg.vehicle_data || null,
    };
    if (createDlg.client_choice === "existing" && !payload.client_id) {
      toast.error("Seleziona un cliente esistente"); return;
    }
    if (createDlg.client_choice === "new" && !payload.new_client?.name?.trim() && !payload.new_client?.surname?.trim()) {
      toast.error("Inserisci nome o cognome del cliente"); return;
    }
    // Validazione intestatario documento vs cliente esistente selezionato (CF mismatch)
    if (createDlg.client_choice === "existing" && payload.client_id) {
      const extractedCF = ((extracted?.owner_fiscal_code || extracted?.fiscal_code || extracted?.cf) || "").toString().toUpperCase().trim();
      const cli = (clientsOptions || []).find((c) => c.id === payload.client_id);
      const clientCF = (cli?.fiscal_code || "").toUpperCase().trim();
      if (extractedCF && clientCF && extractedCF !== clientCF) {
        const proceed = window.confirm(
          `⚠️ ATTENZIONE: i codici fiscali NON combaciano.\n\nDocumento: ${extractedCF}\nCliente selezionato (${cli?.surname || ""} ${cli?.name || ""}): ${clientCF}\n\nVuoi procedere comunque?`,
        );
        if (!proceed) return;
      }
    }
    setBusy(true);
    try {
      const { data } = await api.post(`/inbox/${selected.id}/create-quote`, payload);
      toast.success(`Preventivo creato (${data.attachments_copied} allegati copiati)`);
      setCreateDlg(null);
      setSelected(null); setSelectedId(null);
      load();
      // Naviga al preventivo creato
      if (data.quote_id) setTimeout(() => nav(`/quotes`), 800);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore creazione preventivo"); }
    finally { setBusy(false); }
  };

  return (
    <div className="space-y-4" data-testid="inbox-page">
      <header className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-wider text-slate-500">Documenti ricevuti</p>
          <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight flex items-center gap-2">
            <InboxIcon className="h-6 w-6 text-blue-600" /> Inbox documenti
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Email e documenti inviati dai collaboratori esterni alla tua agenzia.
          </p>
        </div>
        <Button
          onClick={openCompose}
          data-testid="inbox-new-email"
          className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-md"
          size="lg"
        >
          <Send className="h-4 w-4 mr-2" /> Nuova email
        </Button>
      </header>

      {/* Email dedicata agenzia — banner prominente */}
      {myEmail?.enabled && myEmail?.address ? (
        <div
          className="rounded-xl border-2 border-blue-500 bg-gradient-to-br from-blue-50 via-sky-50 to-cyan-50 p-4 sm:p-5 shadow-md"
          data-testid="inbox-my-email-card"
        >
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <div className="h-12 w-12 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0 shadow-md">
                <Mail className="h-6 w-6 text-white" />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] uppercase tracking-[0.18em] text-blue-700 font-bold">
                  Indirizzo email dedicato della tua agenzia
                </div>
                <code
                  className="block text-base sm:text-lg font-mono font-bold text-blue-900 break-all mt-1 select-all"
                  data-testid="inbox-address"
                  onClick={copyEmail}
                  title="Clicca per copiare"
                  style={{ cursor: "copy" }}
                >
                  {myEmail.address}
                </code>
                <p className="text-xs text-slate-700 mt-1.5 leading-relaxed">
                  📨 Condividi questo indirizzo con i tuoi collaboratori esterni — periti, gestori sinistri, broker.
                  Ogni email ricevuta apparirà qui sotto entro 5 minuti.
                </p>
              </div>
            </div>
            <Button
              onClick={copyEmail}
              data-testid="inbox-copy-email"
              className="bg-blue-600 hover:bg-blue-700 text-white shadow-md h-11 px-5 text-sm font-semibold flex-shrink-0"
              size="lg"
            >
              <Copy className="h-4 w-4 mr-2" /> Copia indirizzo
            </Button>
            <Button
              onClick={pollNow}
              disabled={polling}
              data-testid="inbox-poll-now-agency"
              variant="outline"
              className={`border-blue-400 text-blue-700 hover:bg-blue-100 h-11 px-4 text-sm font-semibold flex-shrink-0 relative ${hasNewSignal ? "ring-2 ring-emerald-400 ring-offset-2 animate-pulse bg-emerald-50 border-emerald-500 text-emerald-800" : ""}`}
              size="lg"
              title="Forza un controllo immediato delle email senza aspettare il polling automatico"
            >
              {polling ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}
              {polling ? "Controllo…" : (hasNewSignal ? "🔔 Nuove email!" : "Controlla ora")}
              {hasNewSignal && !polling && (
                <span className="absolute -top-1 -right-1 flex h-3 w-3">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                </span>
              )}
            </Button>
            <Button
              onClick={toggleSound}
              data-testid="inbox-toggle-sound"
              variant="outline"
              size="lg"
              className={`h-11 w-11 p-0 flex-shrink-0 ${soundEnabled ? "border-blue-400 text-blue-700 hover:bg-blue-100" : "border-slate-300 text-slate-400 hover:bg-slate-100"}`}
              title={soundEnabled ? "Suono notifica attivo — clicca per disattivare" : "Suono notifica disattivato — clicca per attivare"}
            >
              {soundEnabled ? <Volume2 className="h-5 w-5" /> : <VolumeX className="h-5 w-5" />}
            </Button>
            <Button
              onClick={togglePush}
              data-testid="inbox-toggle-push"
              variant="outline"
              size="lg"
              className={`h-11 w-11 p-0 flex-shrink-0 ${pushEnabled ? "border-blue-400 text-blue-700 hover:bg-blue-100" : "border-slate-300 text-slate-400 hover:bg-slate-100"}`}
              title={pushEnabled ? "Notifiche desktop attive — clicca per disattivare" : "Notifiche desktop disattivate — clicca per attivare"}
            >
              {pushEnabled ? <Bell className="h-5 w-5" /> : <BellOff className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      ) : (
        <Card className="border-amber-300 bg-amber-50/40">
          <CardContent className="py-3 flex items-center gap-3" data-testid="inbox-my-email-card">
            <Mail className="h-5 w-5 text-amber-600 flex-shrink-0" />
            <div className="text-xs text-amber-700 flex-1" data-testid="inbox-disabled">
              La casella IMAP dell&apos;agenzia non è ancora configurata. Vai su <strong>Impostazioni Email → Ricezione (IMAP)</strong> e inserisci le credenziali della tua casella per ricevere i documenti dei collaboratori.
            </div>
          </CardContent>
        </Card>
      )}

      {/* Template messaggio per collaboratori */}
      {myEmail?.enabled && myEmail?.address && (
        <Card className="border-violet-200 bg-violet-50/30">
          <button
            type="button"
            onClick={() => setTemplateOpen(!templateOpen)}
            data-testid="inbox-template-toggle"
            className="w-full py-3 px-4 flex items-center justify-between gap-3 hover:bg-violet-50/60 transition rounded-t-lg"
          >
            <div className="flex items-center gap-2 text-sm text-violet-900">
              <MessageSquareQuote className="h-4 w-4 text-violet-600" />
              <span className="font-semibold">Modello messaggio pronto</span>
              <span className="text-[11px] text-violet-700 font-normal hidden sm:inline">
                — copia e inoltra ai tuoi collaboratori per spiegare loro come usare l&apos;indirizzo Inbox
              </span>
            </div>
            {templateOpen ? <ChevronUp className="h-4 w-4 text-violet-600" /> : <ChevronDown className="h-4 w-4 text-violet-600" />}
          </button>
          {templateOpen && (
            <CardContent className="pt-0 pb-4 space-y-3">
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  data-testid="inbox-tpl-email"
                  onClick={() => setTemplateChannel("email")}
                  className={`text-xs px-3 py-1 rounded-full border ${templateChannel === "email" ? "bg-violet-600 text-white border-violet-600" : "bg-white border-slate-300 hover:bg-slate-50"}`}
                >
                  📧 Email
                </button>
                <button
                  type="button"
                  data-testid="inbox-tpl-whatsapp"
                  onClick={() => setTemplateChannel("whatsapp")}
                  className={`text-xs px-3 py-1 rounded-full border ${templateChannel === "whatsapp" ? "bg-emerald-600 text-white border-emerald-600" : "bg-white border-slate-300 hover:bg-slate-50"}`}
                >
                  💬 WhatsApp
                </button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={copyTemplate}
                  data-testid="inbox-tpl-copy"
                  className="ml-auto h-7 text-xs"
                >
                  <Copy className="h-3 w-3 mr-1" /> Copia testo
                </Button>
              </div>
              <Textarea
                data-testid="inbox-tpl-textarea"
                readOnly
                value={buildTemplate(templateChannel)}
                rows={templateChannel === "whatsapp" ? 10 : 14}
                className="font-mono text-xs bg-white"
              />
              <p className="text-[11px] text-violet-700 leading-relaxed">
                💡 <strong>Suggerimento:</strong> personalizza il messaggio aggiungendo il tuo nome,
                numero di telefono o eventuali istruzioni specifiche. Una volta inviato,
                ogni allegato che il collaboratore manderà a {" "}
                <code className="font-mono bg-violet-100 px-1 rounded">{myEmail.address}</code>{" "}
                comparirà automaticamente in questa pagina entro 5 minuti.
              </p>
            </CardContent>
          )}
        </Card>
      )}

      {/* Filtri stato + ricerca */}
      <div className="flex items-center gap-2 flex-wrap">
        {[
          { v: "pending", lbl: `📥 Da elaborare (${counts.pending})`, cls: "amber" },
          { v: "processed", lbl: `✓ Elaborate (${counts.processed})`, cls: "emerald" },
          { v: "sent", lbl: `📤 Inviate (${counts.sent})`, cls: "blue" },
          { v: "discarded", lbl: `🗑 Scartate (${counts.discarded})`, cls: "slate" },
          { v: "all", lbl: "🌐 Tutte", cls: "purple" },
          { v: "trash", lbl: `♻️ Cestino${trashCount > 0 ? ` (${trashCount})` : ""}`, cls: "rose" },
        ].map((f) => (
          <button
            key={f.v}
            data-testid={`inbox-filter-${f.v}`}
            onClick={() => setStatusFilter(f.v)}
            className={`text-xs px-3 py-1.5 rounded-full border transition ${
              statusFilter === f.v
                ? `bg-${f.cls}-600 text-white border-${f.cls}-600`
                : "bg-white text-slate-700 border-slate-300 hover:bg-slate-50"
            }`}
          >
            {f.lbl}
          </button>
        ))}
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input
            data-testid="inbox-search"
            placeholder="Cerca per oggetto, mittente o testo…"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            className="pl-8 h-8 text-sm"
          />
        </div>
        {/* 🆕 Toggle vista Lista / Thread */}
        <div className="inline-flex rounded-md border border-slate-300 bg-white overflow-hidden" data-testid="inbox-view-mode-toggle">
          <button
            onClick={() => { setViewMode("list"); try { localStorage.setItem("inbox_view_mode", "list"); } catch { /* ignore */ } }}
            className={`text-xs px-3 py-1.5 transition ${viewMode === "list" ? "bg-slate-800 text-white" : "text-slate-700 hover:bg-slate-50"}`}
            data-testid="inbox-view-list"
            title="Vista lista piatta"
          >📋 Lista</button>
          <button
            onClick={() => { setViewMode("thread"); try { localStorage.setItem("inbox_view_mode", "thread"); } catch { /* ignore */ } }}
            className={`text-xs px-3 py-1.5 transition border-l border-slate-300 ${viewMode === "thread" ? "bg-slate-800 text-white" : "text-slate-700 hover:bg-slate-50"}`}
            data-testid="inbox-view-thread"
            title="Vista conversazioni"
          >💬 Conversazioni</button>
        </div>
      </div>

      {/* Layout 2 colonne: lista + dettaglio */}
      <div className="grid lg:grid-cols-[380px_1fr] gap-4">
        {/* Lista */}
        <Card className="lg:h-[70vh] lg:overflow-y-auto">
          <CardHeader className="sticky top-0 z-10 bg-white border-b">
            <div className="flex items-center justify-between gap-2">
              <CardTitle className="text-sm">
                {viewMode === "thread" && statusFilter !== "trash" ? `Conversazioni (${threads.length})` : `Messaggi (${filtered.length})`}
              </CardTitle>
              {/* 🆕 Toolbar bulk delete — visibile solo in Lista mode con almeno 1 selezione */}
              {(viewMode !== "thread" || statusFilter === "trash") && selectedIds.size > 0 && (
                <div className="flex items-center gap-2" data-testid="inbox-bulk-toolbar">
                  <span className="text-xs font-medium text-slate-600">{selectedIds.size} selezionate</span>
                  <Button
                    size="sm"
                    variant="outline"
                    className="h-7 text-xs text-rose-700 border-rose-300 hover:bg-rose-50"
                    onClick={bulkDeleteSelected}
                    disabled={busy}
                    data-testid="inbox-bulk-delete"
                  >
                    <Trash2 className="h-3 w-3 mr-1" /> {statusFilter === "trash" ? "Cancella def." : "Elimina"}
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="h-7 text-xs text-slate-500"
                    onClick={() => setSelectedIds(new Set())}
                    data-testid="inbox-bulk-clear"
                  >
                    ✕
                  </Button>
                </div>
              )}
              {/* 🆕 Svuota cestino (visibile solo in vista cestino, senza selezione) */}
              {statusFilter === "trash" && emails.length > 0 && selectedIds.size === 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  className="h-7 text-xs text-rose-700 border-rose-300 hover:bg-rose-50"
                  data-testid="inbox-trash-empty"
                  onClick={async () => {
                    if (!window.confirm(`Svuotare COMPLETAMENTE il cestino? Verranno eliminate ${emails.length} email in modo permanente e non recuperabile.`)) return;
                    try {
                      const { data } = await api.post("/messages/trash/empty");
                      toast.success(`Cestino svuotato: ${(data.deleted_inbox || 0) + (data.deleted_outbox || 0)} email eliminate`);
                      setSelected(null); setSelectedId(null);
                      load();
                    } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
                  }}
                >
                  <Trash2 className="h-3 w-3 mr-1" /> Svuota cestino
                </Button>
              )}
            </div>
            {/* 🆕 Riga "Seleziona tutte" — solo in Lista mode con almeno 1 email */}
            {(viewMode !== "thread" || statusFilter === "trash") && filtered.length > 0 && (
              <label className="flex items-center gap-2 text-[11px] text-slate-500 mt-1 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={filtered.length > 0 && filtered.every((e) => selectedIds.has(e.id))}
                  onChange={toggleSelectAll}
                  className="h-3.5 w-3.5"
                  data-testid="inbox-select-all"
                />
                <span>Seleziona tutte le email visibili</span>
              </label>
            )}
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-10 text-slate-400"><Loader2 className="h-5 w-5 animate-spin mr-2" /> Caricamento…</div>
            ) : viewMode === "thread" && statusFilter !== "trash" ? (
              /* 🆕 Vista Thread — raggruppa conversazioni */
              threads.length === 0 ? (
                <div className="py-10 px-4 text-center text-slate-400 text-sm">Nessuna conversazione.</div>
              ) : (
                <ul className="divide-y divide-slate-100" data-testid="inbox-threads-list">
                  {threads.map((th) => {
                    const isOpen = expandedThreads.has(th.thread_key);
                    const hasUnread = (th.unread_count || 0) > 0;
                    return (
                      <li key={th.thread_key}>
                        <button
                          onClick={() => {
                            const next = new Set(expandedThreads);
                            if (next.has(th.thread_key)) next.delete(th.thread_key);
                            else next.add(th.thread_key);
                            setExpandedThreads(next);
                          }}
                          className={`w-full text-left px-4 py-3 border-l-4 ${hasUnread ? "border-amber-500 bg-amber-50/30" : "border-slate-200"} hover:bg-slate-50`}
                          data-testid={`inbox-thread-${th.thread_key.slice(0, 12)}`}
                        >
                          <div className="flex items-center justify-between gap-2">
                            <span className="text-xs text-slate-600 truncate flex-1">
                              {(th.counterparties || []).slice(0, 2).join(", ")}
                              {(th.counterparties || []).length > 2 && <span className="text-slate-400"> +{th.counterparties.length - 2}</span>}
                            </span>
                            <div className="flex items-center gap-1 shrink-0">
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-800 text-white font-semibold">{th.message_count}</span>
                              {th.in_count > 0 && <span className="text-[10px] text-emerald-700" title="ricevute">📥{th.in_count}</span>}
                              {th.out_count > 0 && <span className="text-[10px] text-blue-700" title="inviate">📤{th.out_count}</span>}
                              {hasUnread && <span className="text-[10px] px-1 rounded bg-amber-500 text-white font-bold">{th.unread_count}</span>}
                            </div>
                          </div>
                          <div className="text-sm font-semibold text-slate-900 truncate mt-1">
                            {isOpen ? "▼ " : "▶ "}{th.subject || "(senza oggetto)"}
                          </div>
                          <div className="text-[11px] text-slate-500 mt-1">
                            {th.last_date ? new Date(th.last_date).toLocaleString("it-IT") : "-"}
                          </div>
                        </button>
                        {isOpen && (
                          <ul className="bg-slate-50/70 border-t border-slate-100" data-testid={`thread-messages-${th.thread_key.slice(0, 12)}`}>
                            {th.messages.map((m) => {
                              const isOut = m.direction === "out";
                              return (
                                <li key={m.id}>
                                  <button
                                    onClick={() => loadDetail(m.id, m.direction)}
                                    className={`w-full text-left pl-10 pr-4 py-2 border-l-4 hover:bg-white ${isOut ? "border-blue-300" : "border-emerald-300"} ${selectedId === m.id ? "bg-blue-100" : ""}`}
                                    data-testid={`inbox-thread-msg-${m.id}`}
                                  >
                                    <div className="flex items-center gap-2 text-xs">
                                      <span>{isOut ? "📤" : "📥"}</span>
                                      <span className={`truncate flex-1 ${isOut ? "text-blue-800" : "text-emerald-800"}`}>{m.counterparty}</span>
                                      <span className="text-[10px] text-slate-500 shrink-0">{m.date ? new Date(m.date).toLocaleDateString("it-IT") : ""}</span>
                                    </div>
                                    <div className="text-[11px] text-slate-600 truncate mt-0.5">{(m.body_text || "").slice(0, 100)}</div>
                                  </button>
                                </li>
                              );
                            })}
                          </ul>
                        )}
                      </li>
                    );
                  })}
                </ul>
              )
            ) : filtered.length === 0 ? (
              <div className="py-10 px-4 text-center text-slate-400 text-sm">
                {statusFilter === "pending" ? "Nessuna email da elaborare." : "Nessun risultato."}
              </div>
            ) : (
              <ul className="divide-y divide-slate-100">
                {filtered.map((e) => {
                  const st = STATUS_BADGE[e.status] || STATUS_BADGE.pending;
                  const isSel = selectedId === e.id;
                  const isOutbound = e.direction === "out";
                  // 🆕 Colori/icone distinti per direzione (senza spezzare il layout esistente)
                  const dirIcon = isOutbound ? "📤" : "📥";
                  const dirLbl = isOutbound ? "Inviata" : "Ricevuta";
                  const dirCls = isOutbound
                    ? "border-blue-400 bg-blue-50/40"
                    : "border-emerald-400 bg-emerald-50/40";
                  const contact = isOutbound ? e.to : e.from_addr;
                  return (
                    <li key={`${e.direction || "in"}-${e.id}`} className="flex items-stretch">
                      {/* 🆕 Checkbox per selezione multipla — cliccarla non apre la mail */}
                      <label
                        className="flex items-center justify-center px-2 border-l-4 border-transparent hover:bg-slate-50 cursor-pointer"
                        onClick={(evt) => evt.stopPropagation()}
                      >
                        <input
                          type="checkbox"
                          className="h-4 w-4"
                          checked={selectedIds.has(e.id)}
                          onChange={() => toggleSelectedId(e.id)}
                          onClick={(evt) => evt.stopPropagation()}
                          data-testid={`inbox-check-${e.id}`}
                        />
                      </label>
                      <button
                        data-testid={`inbox-item-${e.id}`}
                        onClick={() => loadDetail(e.id, e.direction)}
                        className={`flex-1 text-left px-4 py-3 transition border-l-4 ${isSel ? "bg-blue-50 border-blue-600" : `hover:bg-slate-50 ${dirCls}`}`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-xs font-medium truncate flex-1 flex items-center gap-1">
                            <span title={dirLbl} aria-label={dirLbl}>{dirIcon}</span>
                            <span className={`truncate ${isOutbound ? "text-blue-800" : "text-emerald-800"}`}>{contact || "—"}</span>
                          </span>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded border ${isOutbound ? "bg-blue-100 text-blue-800 border-blue-200" : st.cls} whitespace-nowrap`}>
                            {isOutbound ? "Inviata" : st.lbl}
                          </span>
                        </div>
                        <div className="text-sm font-semibold text-slate-900 truncate mt-1">{e.subject}</div>
                        <div className="flex items-center gap-2 text-[11px] text-slate-500 mt-1">
                          <span>{e.received_at ? new Date(e.received_at).toLocaleString("it-IT") : "-"}</span>
                          {(e.attachments || []).length > 0 && (
                            <span className="flex items-center gap-0.5"><Paperclip className="h-3 w-3" /> {e.attachments.length}</span>
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Dettaglio */}
        <Card className="lg:h-[70vh] lg:overflow-y-auto relative">
          {!selectedId ? (
            <div className="h-full flex items-center justify-center text-slate-400 text-sm py-20">
              Seleziona un&apos;email a sinistra per vedere il contenuto.
            </div>
          ) : !selected ? (
            <div className="h-full flex items-center justify-center text-slate-400 py-20">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Caricamento…
            </div>
          ) : (
            <div className="p-5 space-y-4">
              <header className="border-b pb-3">
                <div className="flex items-start justify-between gap-2">
                  <h2 className="font-display text-lg font-semibold text-slate-900" data-testid="inbox-detail-subject">
                    {selected.direction === "out" ? "📤 " : "📥 "}{selected.subject}
                  </h2>
                  <span className={`text-[10px] px-2 py-0.5 rounded border whitespace-nowrap ${
                    selected.direction === "out"
                      ? "bg-blue-100 text-blue-800 border-blue-200"
                      : STATUS_BADGE[selected.status]?.cls
                  }`}>
                    {selected.direction === "out" ? "Inviata" : STATUS_BADGE[selected.status]?.lbl}
                  </span>
                </div>
                <div className="text-xs text-slate-600 mt-1">
                  {selected.direction === "out"
                    ? (<><strong>A:</strong> {selected.to || "—"}</>)
                    : (<><strong>Da:</strong> {selected.from_addr}</>)
                  }
                </div>
                <div className="text-xs text-slate-500">
                  {selected.direction === "out" ? "Inviata" : "Ricevuta"}: {selected.received_at ? new Date(selected.received_at).toLocaleString("it-IT") : "-"}
                </div>
                {selected.linked_quote_id && (
                  <div className="mt-1 text-xs text-emerald-700">
                    ✓ Collegata al preventivo <code className="font-mono">{selected.linked_quote_id.slice(0, 8)}</code>
                    {selected.link_source && (
                      <span className="ml-1 text-[10px] text-emerald-600">
                        · {({ quote_number: "🔗 Numero PR", client_email: "👤 Email cliente", broker_email: "🏢 Email broker" }[selected.link_source] || selected.link_source)}
                      </span>
                    )}
                    {Array.isArray(selected.linked_quote_ids) && selected.linked_quote_ids.length > 1 && (
                      <span className="ml-1 text-[10px] text-emerald-600">
                        (+{selected.linked_quote_ids.length - 1} altr{selected.linked_quote_ids.length === 2 ? "o" : "i"})
                      </span>
                    )}
                  </div>
                )}
              </header>

              {/* 🆕 09/07/2026 — Barra azioni STICKY in cima: sempre visibile anche con email lunghe */}
              <div className="sticky top-0 z-10 -mx-5 px-5 py-2 bg-white/95 backdrop-blur border-b border-slate-200 shadow-sm">
                <div className="flex items-center gap-2 flex-wrap">
                  <Button
                    size="sm"
                    data-testid="inbox-reply-top"
                    onClick={openReply}
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                  >
                    <Reply className="h-4 w-4 mr-1.5" /> Rispondi
                  </Button>
                  {selected.status === "pending" && (
                    <>
                      <Button
                        size="sm"
                        data-testid="inbox-create-quote-top"
                        onClick={openCreateQuote}
                        className="bg-blue-600 hover:bg-blue-700 text-white"
                      >
                        <FileText className="h-4 w-4 mr-1.5" /> Crea preventivo
                      </Button>
                      <Button
                        size="sm"
                        data-testid="inbox-discard-top"
                        onClick={discardEmail}
                        variant="outline"
                        disabled={busy}
                        className="text-rose-600 hover:bg-rose-50"
                      >
                        <XCircle className="h-4 w-4 mr-1.5" /> Scarta
                      </Button>
                    </>
                  )}
                  {(selected.status === "discarded" || selected.status === "processed") && (
                    <Button
                      size="sm"
                      data-testid="inbox-restore-top"
                      onClick={restoreEmail}
                      variant="outline"
                      disabled={busy}
                      className="text-blue-700 border-blue-300 hover:bg-blue-50"
                      title="Riporta l'email tra le Da elaborare"
                    >
                      ↩️ Ripristina
                    </Button>
                  )}
                </div>
              </div>

              {/* Allegati */}
              {(selected.attachments || []).length > 0 && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-2">
                    Allegati ({selected.attachments.length})
                  </div>
                  <ul className="space-y-1.5">
                    {selected.attachments.map((a) => (
                      <li key={a.id} className="flex items-center justify-between gap-2 border border-slate-200 rounded-md px-3 py-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <FileText className="h-4 w-4 text-slate-500 flex-shrink-0" />
                          <span className="text-sm truncate" data-testid={`inbox-att-name-${a.id}`}>{a.filename}</span>
                          <span className="text-[10px] text-slate-400">{formatFileSize(a.size)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs text-emerald-700 hover:bg-emerald-50 border-emerald-200"
                            data-testid={`inbox-att-preview-${a.id}`}
                            onClick={() => openAttachmentPreview(
                              `/inbox/${selected.id}/attachments/${a.id}/download`,
                              { filename: a.filename, content_type: a.content_type }
                            )}
                            title="Anteprima inline"
                          >
                            <Eye className="h-3 w-3 mr-1" /> Anteprima
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs"
                            data-testid={`inbox-att-download-${a.id}`}
                            onClick={() => downloadWithProgress({
                              label: a.filename || "allegato",
                              blobPath: `/inbox/${selected.id}/attachments/${a.id}/download`,
                              filename: a.filename,
                            }).catch(() => {})}
                          >
                            <ExternalLink className="h-3 w-3 mr-1" /> Apri
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-7 text-xs text-violet-700 hover:bg-violet-50"
                            data-testid={`inbox-extract-${a.id}`}
                            disabled={extractingAttId === a.id || selected.status !== "pending"}
                            onClick={() => extractFromAttachment(a.id)}
                            title="Estrai dati dal libretto con AI"
                          >
                            {extractingAttId === a.id ? <Loader2 className="h-3 w-3 mr-1 animate-spin" /> : <Sparkles className="h-3 w-3 mr-1" />}
                            Estrai libretto
                          </Button>
                          {/* 🆕 Salva allegato nell'archivio documenti di un preventivo collegato */}
                          {selected.linked_quote_id && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 text-xs text-emerald-700 hover:bg-emerald-50 border-emerald-200"
                              data-testid={`inbox-att-save-${a.id}`}
                              onClick={() => setSaveAttDialog({ att: a, quote_id: selected.linked_quote_id, filename: a.filename })}
                              title="Salva questo allegato nel preventivo collegato"
                            >
                              <Save className="h-3 w-3 mr-1" /> Salva nel Preventivo
                            </Button>
                          )}
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Dati estratti */}
              {extracted && (
                <div className="rounded-md border border-violet-300 bg-violet-50/60 p-3" data-testid="inbox-extracted">
                  <div className="text-xs font-semibold text-violet-900 mb-2 flex items-center gap-1">
                    <Sparkles className="h-3.5 w-3.5" /> Dati estratti dal libretto (anteprima)
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-3 gap-y-1 text-xs">
                    {Object.entries(extracted).filter(([, v]) => v !== null && v !== "" && v !== undefined).map(([k, v]) => (
                      <div key={k} className="border-b border-violet-200/50 pb-0.5">
                        <span className="text-violet-700 uppercase text-[10px] font-medium tracking-wide">{k.replace(/_/g, " ")}</span>
                        <div className="font-mono text-slate-800 truncate">{String(v)}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Corpo email — render HTML se disponibile, altrimenti plain text */}
              {(selected.body_html || selected.body_text) && (
                <div>
                  <div className="text-xs uppercase tracking-wide text-slate-500 font-semibold mb-1 flex items-center justify-between">
                    <span>Testo email</span>
                    {selected.body_html && selected.body_text && (
                      <button
                        type="button"
                        data-testid="inbox-toggle-view"
                        onClick={() => setBodyViewMode((m) => (m === "html" ? "text" : "html"))}
                        className="text-[10px] text-blue-600 hover:underline"
                      >
                        {bodyViewMode === "html" ? "📝 Vedi testo semplice" : "🎨 Vedi HTML formattato"}
                      </button>
                    )}
                  </div>
                  {bodyViewMode === "html" && selected.body_html ? (
                    <div
                      className="email-html-body text-sm bg-white border border-slate-200 rounded-md p-4 max-h-[500px] overflow-y-auto"
                      data-testid="inbox-body-html"
                      dangerouslySetInnerHTML={{ __html: sanitizeEmailHtml(selected.body_html) }}
                    />
                  ) : (
                    <pre className="text-xs whitespace-pre-wrap bg-slate-50 border border-slate-200 rounded-md p-3 max-h-[500px] overflow-y-auto font-sans" data-testid="inbox-body-text">
                      {selected.body_text || "(nessun testo semplice disponibile)"}
                    </pre>
                  )}
                </div>
              )}

              {/* Azioni */}
              <div className="flex items-center gap-2 pt-2 border-t flex-wrap">
                <Button
                  data-testid="inbox-reply"
                  onClick={openReply}
                  className="bg-emerald-600 hover:bg-emerald-700 text-white"
                >
                  <Reply className="h-4 w-4 mr-1.5" /> Rispondi
                </Button>
                {selected.status === "pending" && (
                  <>
                    <Button
                      data-testid="inbox-create-quote"
                      onClick={openCreateQuote}
                      className="bg-blue-600 hover:bg-blue-700 text-white"
                    >
                      <FileText className="h-4 w-4 mr-1.5" /> Crea preventivo da questa email
                    </Button>
                    <Button
                      data-testid="inbox-discard"
                      onClick={discardEmail}
                      variant="outline"
                      disabled={busy}
                      className="text-rose-600 hover:bg-rose-50"
                    >
                      <XCircle className="h-4 w-4 mr-1.5" /> Scarta
                    </Button>
                  </>
                )}
                {(selected.status === "discarded" || selected.status === "processed") && (
                  <Button
                    data-testid="inbox-restore"
                    onClick={restoreEmail}
                    variant="outline"
                    disabled={busy}
                    className="text-blue-700 border-blue-300 hover:bg-blue-50"
                    title="Riporta l'email tra le Da elaborare"
                  >
                    <Reply className="h-4 w-4 mr-1.5 rotate-180" /> Ripristina
                  </Button>
                )}
                {/* 🆕 Elimina definitivamente (sia in che out) — spinta a destra */}
                {statusFilter === "trash" ? (
                  <>
                    <Button
                      data-testid="inbox-trash-restore"
                      variant="outline"
                      className="ml-auto text-emerald-700 border-emerald-300 hover:bg-emerald-50"
                      onClick={async () => {
                        if (!selected) return;
                        try {
                          await api.post("/messages/trash/restore", { id: selected.id, direction: selected.direction });
                          toast.success("Email ripristinata dal cestino");
                          setSelected(null); setSelectedId(null);
                          load();
                        } catch (e) { toast.error(e?.response?.data?.detail || "Errore ripristino"); }
                      }}
                    >
                      ♻️ Ripristina
                    </Button>
                    <Button
                      data-testid="inbox-trash-hard-delete"
                      variant="outline"
                      className="text-rose-700 border-rose-300 hover:bg-rose-50"
                      onClick={async () => {
                        if (!selected) return;
                        if (!window.confirm("Eliminare DEFINITIVAMENTE questa email? L'operazione è irreversibile.")) return;
                        try {
                          await api.delete(`/messages/trash/${selected.id}?direction=${selected.direction}`);
                          toast.success("Email eliminata definitivamente");
                          setSelected(null); setSelectedId(null);
                          load();
                        } catch (e) { toast.error(e?.response?.data?.detail || "Errore eliminazione"); }
                      }}
                    >
                      <Trash2 className="h-4 w-4 mr-1.5" /> Elimina definitivamente
                    </Button>
                  </>
                ) : (
                  <Button
                    data-testid="inbox-delete"
                    onClick={deleteEmail}
                    variant="outline"
                    disabled={busy}
                    className="ml-auto text-rose-700 border-rose-300 hover:bg-rose-50"
                    title={selected.direction === "out" ? "Sposta questa email inviata nel cestino" : "Sposta questa email ricevuta nel cestino"}
                  >
                    <Trash2 className="h-4 w-4 mr-1.5" /> Sposta nel cestino
                  </Button>
                )}
              </div>
            </div>
          )}
        </Card>
      </div>

      {/* Dialog Rispondi a email */}
      <Dialog open={replyOpen} onOpenChange={(o) => !o && setReplyOpen(false)}>
        <DialogContent className="max-w-2xl w-[95vw] h-[92vh] max-h-[92vh] flex flex-col p-0 overflow-hidden" data-testid="inbox-reply-dialog">
          <DialogHeader className="px-6 pt-6 pb-2 border-b flex-shrink-0">
            <DialogTitle className="flex items-center gap-2">
              {replyMode === "compose" ? (
                <><Send className="h-5 w-5 text-emerald-600" /> Nuova email</>
              ) : (
                <><Reply className="h-5 w-5 text-emerald-600" /> Rispondi all&apos;email</>
              )}
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto px-6 py-4 space-y-3">
            {replyMode === "compose" && (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-2.5">
                <Label className="text-xs font-semibold flex items-center gap-1.5"><Search className="h-3 w-3" /> Cerca cliente dalla rubrica</Label>
                <Input
                  data-testid="compose-client-search"
                  value={composeClientSearch}
                  onChange={(e) => setComposeClientSearch(e.target.value)}
                  placeholder="Nome, cognome o email cliente…"
                  className="mt-1 text-xs"
                />
                {composeClientOptions.length > 0 && (
                  <ul className="mt-2 max-h-32 overflow-y-auto bg-white rounded border border-slate-200 divide-y divide-slate-100">
                    {composeClientOptions.map((c) => (
                      <li key={c.id}>
                        <button
                          type="button"
                          data-testid={`compose-client-pick-${c.id}`}
                          onClick={() => {
                            setReplyForm((prev) => ({ ...prev, to: c.email }));
                            setComposeClientSearch("");
                            setComposeClientOptions([]);
                          }}
                          className="w-full text-left px-2 py-1 text-xs hover:bg-emerald-50"
                        >
                          <div className="font-medium">{c.surname} {c.name}</div>
                          <div className="text-[10px] text-slate-500">{c.email}</div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
            <div>
              <Label className="text-xs font-semibold">A *</Label>
              <Input
                data-testid="reply-to"
                value={replyForm.to}
                onChange={(e) => setReplyForm({ ...replyForm, to: e.target.value })}
                placeholder="destinatario@dominio.it (separa più indirizzi con virgola)"
              />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              <div>
                <Label className="text-xs font-semibold">CC</Label>
                <Input
                  data-testid="reply-cc"
                  value={replyForm.cc}
                  onChange={(e) => setReplyForm({ ...replyForm, cc: e.target.value })}
                  placeholder="opzionale"
                />
              </div>
              <div>
                <Label className="text-xs font-semibold">BCC</Label>
                <Input
                  data-testid="reply-bcc"
                  value={replyForm.bcc}
                  onChange={(e) => setReplyForm({ ...replyForm, bcc: e.target.value })}
                  placeholder="opzionale"
                />
              </div>
            </div>
            {replyMode !== "reply" && (
              <label className="flex items-start gap-2 text-xs bg-amber-50 border border-amber-200 rounded p-2 cursor-pointer" data-testid="compose-commercial-label">
                <input type="checkbox" className="mt-0.5" checked={!!replyForm.commercial} onChange={(e) => setReplyForm({ ...replyForm, commercial: e.target.checked })} data-testid="compose-commercial" />
                <span><b>Comunicazione commerciale</b> — invia solo ai clienti con consenso marketing registrato; gli altri vengono esclusi in automatico e in fondo all&apos;email viene aggiunta la nota per la revoca.</span>
              </label>
            )}
            <div>
              <Label className="text-xs font-semibold">Oggetto *</Label>
              <Input
                data-testid="reply-subject"
                value={replyForm.subject}
                onChange={(e) => setReplyForm({ ...replyForm, subject: e.target.value })}
              />
            </div>
            <div>
              <Label className="text-xs font-semibold">Messaggio</Label>
              <div className="border border-slate-200 rounded-md bg-white" data-testid="reply-body-quill" style={{ minHeight: 280 }}>
                <ReactQuill
                  theme="snow"
                  value={replyForm.body_html}
                  onChange={(html) => setReplyForm({ ...replyForm, body_html: html })}
                  modules={REPLY_QUILL_MODULES}
                  formats={REPLY_QUILL_FORMATS}
                  placeholder="Scrivi qui la tua risposta…"
                  style={{ minHeight: 220 }}
                />
              </div>
              <p className="text-[10px] text-slate-400 mt-1">La firma agenzia viene aggiunta automaticamente in fondo all&apos;email.</p>
            </div>

            {/* Allegati ricevuti — inoltrabili (solo in modalità reply) */}
            {replyMode === "reply" && selected?.attachments && selected.attachments.length > 0 && (
              <div className="rounded-md border border-slate-200 bg-slate-50 p-2.5">
                <div className="text-[11px] font-semibold text-slate-700 mb-1">Inoltra allegati ricevuti:</div>
                <ul className="space-y-1">
                  {selected.attachments.map((a) => (
                    <li key={a.id} className="flex items-center gap-2 text-xs">
                      <input
                        type="checkbox"
                        data-testid={`reply-forward-att-${a.id}`}
                        checked={(replyForm.inbox_attachment_ids || []).includes(a.id)}
                        onChange={() => toggleInboxAttachmentForReply(a.id)}
                        className="h-3.5 w-3.5"
                      />
                      <Paperclip className="h-3 w-3 text-slate-500" />
                      <span className="truncate flex-1">{a.filename}</span>
                      <span className="text-[10px] text-slate-400">{formatFileSize(a.size)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {/* Allegati aggiunti */}
            <div>
              <Label className="text-xs font-semibold">Allegati aggiuntivi</Label>
              <div className="flex items-center gap-2 mt-1">
                <input
                  type="file"
                  data-testid="reply-upload-attachment"
                  onChange={(e) => { const f = e.target.files?.[0]; uploadReplyAttachment(f); e.target.value = ""; }}
                  className="text-xs"
                  disabled={replyAttUploading}
                />
                {replyAttUploading && <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-600" />}
              </div>
              {replyAttachments.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {replyAttachments.map((a) => (
                    <li key={a.id} className="flex items-center justify-between gap-2 text-xs bg-blue-50 border border-blue-200 rounded px-2 py-1">
                      <span className="truncate"><Paperclip className="h-3 w-3 inline mr-1" /> {a.filename}</span>
                      <span className="text-[10px] text-slate-500">{formatFileSize(a.size)}</span>
                      <button
                        type="button"
                        onClick={() => removeReplyAttachment(a.id)}
                        data-testid={`reply-att-remove-${a.id}`}
                        className="text-rose-600 hover:bg-rose-100 rounded p-0.5"
                        title="Rimuovi"
                      >
                        <XIcon className="h-3 w-3" />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
          <DialogFooter className="px-6 py-3 border-t flex-shrink-0 bg-white">
            <Button variant="outline" onClick={() => setReplyOpen(false)} disabled={replySending}>Annulla</Button>
            <Button
              data-testid="reply-send"
              onClick={sendReply}
              disabled={replySending}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
            >
              {replySending ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              {replyMode === "compose" ? "Invia email" : "Invia risposta"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog crea preventivo */}
      <Dialog open={!!createDlg} onOpenChange={(o) => !o && setCreateDlg(null)}>
        <DialogContent className="max-w-lg" data-testid="inbox-create-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-600" /> Crea preventivo da email
            </DialogTitle>
          </DialogHeader>
          {createDlg && (
            <div className="space-y-3">
              {/* Step 1: scelta cliente */}
              <div className="space-y-2">
                <Label className="text-xs font-semibold">Cliente</Label>
                <div className="flex gap-2">
                  <button
                    type="button"
                    data-testid="inbox-client-new"
                    onClick={() => setCreateDlg({ ...createDlg, client_choice: "new" })}
                    className={`text-xs px-3 py-1.5 rounded border ${createDlg.client_choice === "new" ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-300 hover:bg-slate-50"}`}
                  >
                    Crea nuovo cliente
                  </button>
                  <button
                    type="button"
                    data-testid="inbox-client-existing"
                    onClick={() => setCreateDlg({ ...createDlg, client_choice: "existing" })}
                    className={`text-xs px-3 py-1.5 rounded border ${createDlg.client_choice === "existing" ? "bg-blue-600 text-white border-blue-600" : "bg-white border-slate-300 hover:bg-slate-50"}`}
                  >
                    Cliente esistente
                  </button>
                </div>
              </div>

              {createDlg.client_choice === "new" && (
                <div className="space-y-2 rounded-md border border-blue-200 bg-blue-50/30 p-3">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[11px]">Nome</Label>
                      <Input
                        data-testid="inbox-new-client-name"
                        value={createDlg.new_client.name}
                        onChange={(e) => setCreateDlg({ ...createDlg, new_client: { ...createDlg.new_client, name: e.target.value } })}
                      />
                    </div>
                    <div>
                      <Label className="text-[11px]">Cognome</Label>
                      <Input
                        data-testid="inbox-new-client-surname"
                        value={createDlg.new_client.surname}
                        onChange={(e) => setCreateDlg({ ...createDlg, new_client: { ...createDlg.new_client, surname: e.target.value } })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    <div>
                      <Label className="text-[11px]">Codice fiscale</Label>
                      <Input
                        data-testid="inbox-new-client-cf"
                        value={createDlg.new_client.fiscal_code}
                        onChange={(e) => setCreateDlg({ ...createDlg, new_client: { ...createDlg.new_client, fiscal_code: e.target.value.toUpperCase() } })}
                        className="font-mono uppercase"
                      />
                    </div>
                    <div>
                      <Label className="text-[11px]">Cellulare</Label>
                      <MobileInputWithWA
                        value={createDlg.new_client.phone}
                        onChange={(v) => setCreateDlg({ ...createDlg, new_client: { ...createDlg.new_client, phone: v } })}
                        testid="inbox-new-client-phone"
                        clientName={`${createDlg.new_client.name || ""} ${createDlg.new_client.surname || ""}`.trim()}
                        placeholder="Es. 3331234567"
                      />
                    </div>
                  </div>
                  <div>
                    <Label className="text-[11px]">Email</Label>
                    <Input
                      data-testid="inbox-new-client-email"
                      type="email"
                      value={createDlg.new_client.email}
                      onChange={(e) => setCreateDlg({ ...createDlg, new_client: { ...createDlg.new_client, email: e.target.value.toLowerCase() } })}
                    />
                  </div>
                  {extracted && (
                    <p className="text-[10px] text-violet-600">
                      ✨ Pre-compilato dai dati estratti dal libretto. Modifica se necessario.
                    </p>
                  )}
                </div>
              )}

              {createDlg.client_choice === "existing" && (
                <div className="space-y-2 rounded-md border border-emerald-200 bg-emerald-50/30 p-3">
                  <Label className="text-[11px]">Cerca cliente (nome, cognome, CF, email)</Label>
                  <Input
                    data-testid="inbox-search-client"
                    value={clientSearch}
                    onChange={(e) => searchClients(e.target.value)}
                    placeholder="Min 2 caratteri…"
                  />
                  {clientsOptions.length > 0 && (
                    <ul className="border border-slate-200 rounded-md bg-white max-h-40 overflow-y-auto">
                      {clientsOptions.map((c) => (
                        <li key={c.id}>
                          <button
                            type="button"
                            data-testid={`inbox-pick-client-${c.id}`}
                            onClick={() => setCreateDlg({ ...createDlg, existing_client_id: c.id, _client_label: `${c.surname || ""} ${c.name || ""}`.trim() })}
                            className={`w-full text-left text-xs px-3 py-2 hover:bg-emerald-50 ${createDlg.existing_client_id === c.id ? "bg-emerald-100" : ""}`}
                          >
                            <div className="font-medium">{c.surname} {c.name}</div>
                            <div className="text-[10px] text-slate-500">{c.fiscal_code || "—"} · {c.email || c.phone || "—"}</div>
                          </button>
                        </li>
                      ))}
                    </ul>
                  )}
                  {createDlg.existing_client_id && (
                    <p className="text-[11px] text-emerald-700">✓ Selezionato: {createDlg._client_label}</p>
                  )}
                </div>
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <Label className="text-[11px]">Tipo preventivo</Label>
                  <select
                    data-testid="inbox-quote-type"
                    className="w-full h-10 rounded-md border border-slate-300 px-3 text-sm bg-white"
                    value={createDlg.quote_type}
                    onChange={(e) => setCreateDlg({ ...createDlg, quote_type: e.target.value })}
                  >
                    <option value="auto">Auto/Moto</option>
                    <option value="casa">Casa</option>
                    <option value="vita">Vita</option>
                    <option value="salute">Salute</option>
                    <option value="altro">Altro</option>
                  </select>
                </div>
                <div>
                  <Label className="text-[11px]">Allegati da copiare</Label>
                  <div className="text-xs bg-slate-50 border border-slate-200 rounded px-2 py-2.5">
                    {(createDlg.attachment_ids || []).length} / {(selected?.attachments || []).length} selezionati
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-[11px]">Note</Label>
                <Textarea
                  data-testid="inbox-quote-notes"
                  rows={2}
                  value={createDlg.notes}
                  onChange={(e) => setCreateDlg({ ...createDlg, notes: e.target.value })}
                />
              </div>

              {/* Selezione allegati */}
              <div className="border-t pt-2">
                <Label className="text-[11px] font-semibold">Allegati da copiare nel preventivo</Label>
                <ul className="mt-1 space-y-1 max-h-32 overflow-y-auto">
                  {(selected?.attachments || []).map((a) => {
                    const checked = (createDlg.attachment_ids || []).includes(a.id);
                    return (
                      <li key={a.id}>
                        <label className="flex items-center gap-2 text-xs cursor-pointer hover:bg-slate-50 px-2 py-1 rounded">
                          <input
                            type="checkbox"
                            data-testid={`inbox-att-pick-${a.id}`}
                            checked={checked}
                            onChange={(e) => {
                              const cur = createDlg.attachment_ids || [];
                              const next = e.target.checked ? [...cur, a.id] : cur.filter((x) => x !== a.id);
                              setCreateDlg({ ...createDlg, attachment_ids: next });
                            }}
                            className="h-3.5 w-3.5"
                          />
                          <FileText className="h-3 w-3 text-slate-400" />
                          <span className="truncate">{a.filename}</span>
                        </label>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateDlg(null)}>Annulla</Button>
            <Button
              data-testid="inbox-create-confirm"
              onClick={confirmCreateQuote}
              disabled={busy}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Crea preventivo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 🆕 Dialog "Salva allegato nel Preventivo" con rinomina e selezione preventivo (se multipli linkati) */}
      <Dialog open={!!saveAttDialog} onOpenChange={(o) => { if (!o && !savingAttToQuote) setSaveAttDialog(null); }}>
        <DialogContent className="max-w-md" data-testid="inbox-save-att-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-700">
              <Save className="h-5 w-5" /> Salva allegato nel Preventivo
            </DialogTitle>
          </DialogHeader>
          {saveAttDialog && (
            <div className="space-y-3">
              <div className="text-xs text-slate-600 border border-slate-200 rounded p-2 bg-slate-50">
                📎 Originale: <strong>{saveAttDialog.att.filename}</strong>
                <div className="text-[10px] text-slate-500 mt-0.5">{formatFileSize(saveAttDialog.att.size)}</div>
              </div>
              {/* 🆕 Feb 2026 — Selettore preventivo di destinazione: mostrato SEMPRE se
                  c'è almeno un preventivo collegato all'email. Label = "N° preventivo — Cliente". */}
              {Array.isArray(selected?.linked_quote_ids) && selected.linked_quote_ids.length >= 1 && (
                <div>
                  <Label className="text-xs">Preventivo di destinazione</Label>
                  <select
                    data-testid="inbox-save-att-quote-select"
                    value={saveAttDialog.quote_id}
                    onChange={(e) => setSaveAttDialog({ ...saveAttDialog, quote_id: e.target.value })}
                    className="w-full mt-1 border border-slate-300 rounded px-2 py-1.5 text-sm"
                  >
                    {selected.linked_quote_ids.map((qid) => {
                      const info = linkedQuotesInfo[qid] || {};
                      const number = info.number ? `N° ${info.number}` : (info.missing ? "Preventivo eliminato" : `${qid.slice(0, 8)}…`);
                      const clientPart = info.client_name ? ` — ${info.client_name}` : "";
                      const trashTag = info.deleted ? " (nel cestino)" : "";
                      return (
                        <option key={qid} value={qid} disabled={!!info.deleted || !!info.missing}>
                          {number}{clientPart}{trashTag}
                        </option>
                      );
                    })}
                  </select>
                  {selected.linked_quote_ids.length === 1 && (
                    <div className="text-[10px] text-slate-400 mt-1">
                      Un solo preventivo collegato — puoi confermare la destinazione.
                    </div>
                  )}
                </div>
              )}
              <div>
                <Label className="text-xs">Nuovo nome file <span className="text-slate-400 font-normal">(estensione preservata)</span></Label>
                <Input
                  data-testid="inbox-save-att-filename"
                  value={saveAttDialog.filename}
                  onChange={(e) => setSaveAttDialog({ ...saveAttDialog, filename: e.target.value })}
                  className="text-sm h-8 mt-1"
                  autoFocus
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveAttDialog(null)} disabled={savingAttToQuote}>
              Annulla
            </Button>
            <Button
              data-testid="inbox-save-att-confirm"
              disabled={savingAttToQuote || !saveAttDialog?.filename?.trim()}
              className="bg-emerald-600 hover:bg-emerald-700 text-white"
              onClick={async () => {
                if (!saveAttDialog) return;
                setSavingAttToQuote(true);
                try {
                  const { data } = await api.post(
                    `/inbox/${selected.id}/attachments/${saveAttDialog.att.id}/save-to-quote`,
                    { quote_id: saveAttDialog.quote_id, filename: saveAttDialog.filename.trim() }
                  );
                  toast.success(`Allegato salvato nel preventivo come "${data.filename}"`);
                  setSaveAttDialog(null);
                } catch (e) {
                  toast.error(e?.response?.data?.detail || "Errore durante il salvataggio");
                } finally {
                  setSavingAttToQuote(false);
                }
              }}
            >
              {savingAttToQuote ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Salva nel preventivo
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* 🆕 Anteprima inline PDF/immagini allegati email */}
      {AttachmentPreviewDialog}
    </div>
  );
}
